import { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAutocomplete } from '../hooks/useAutocomplete';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from './navbar';
import Footer from './Footer';
import {
  FiTrendingUp, FiTrendingDown, FiZap, FiClock,
  FiBarChart2, FiMapPin, FiSearch, FiExternalLink,
} from 'react-icons/fi';
import { STARTUP_API_URL, BASE_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import RippleWaveLoader from './ui/RippleWaveLoader';
import { useProgressiveSearch } from '../hooks/useProgressiveSearch';
import axios from 'axios';

/* ── constants ─────────────────────────────────────────────── */
const TRENDS_LOADING_MESSAGES = [
  'Searching latest 2025 AI trends from the web…',
  'Analysing industry adoption patterns…',
  'Compiling sector-wise trend data…',
  'Extracting key AI breakthroughs…',
];

const SECTORS = [
  'Healthcare', 'Finance', 'Manufacturing', 'Retail', 'Logistics',
  'Education', 'Energy', 'Agriculture', 'Government', 'Media',
];

const GEOGRAPHIES = [
  'India', 'United States', 'Europe', 'Southeast Asia', 'Middle East', 'Global',
];

const GEO_CODE: Record<string, string> = {
  'India': 'IN', 'United States': 'US', 'Europe': 'GB',
  'Southeast Asia': 'SG', 'Middle East': 'AE', 'Global': 'US',
};

const QUICK_PROMPTS = [
  { label: 'Agentic AI in India',          query: 'Agentic AI',          geo: 'India' },
  { label: 'AI in Indian Manufacturing',   query: 'AI in Manufacturing',  geo: 'India' },
  { label: 'GenAI for Indian Enterprise',  query: 'GenAI for Enterprise', geo: 'India' },
  { label: 'Edge AI India',                query: 'Edge AI',              geo: 'India' },
  { label: 'Digital Twins India',          query: 'Digital Twins',        geo: 'India' },
  { label: 'AI Copilots India',            query: 'AI Copilots',          geo: 'India' },
];

/* ── interfaces ────────────────────────────────────────────── */
interface UseCaseTrend {
  id?: number;
  usecase_title: string;
  description?: string;
  sector?: string;
  trend_score?: number;
  momentum?: string;
  prediction_timeframe?: string;
  confidence?: number;
  drivers?: string[];
  barriers?: string[];
  market_size?: string;
  growth_rate?: string;
  key_players?: string[];
  adoption_stage?: string;
}

/* ── url helpers ───────────────────────────────────────────── */
const hostLabel = (url?: string) => {
  try { return new URL(url || '').hostname.replace('www.', ''); } catch { return ''; }
};

/* ══════════════════════════════════════════════════════════════ */
const UseCaseTrendsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const webSearch = useProgressiveSearch();

  const [searchQuery, setSearchQuery]   = useState('');
  const [sector, setSector]             = useState('');
  const [geography, setGeography]       = useState('India');
  const [trends, setTrends]             = useState<UseCaseTrend[]>([]);
  const [loading, setLoading]           = useState(false);
  const [hasSearched, setHasSearched]   = useState(false);
  const [selectedTrend, setSelectedTrend] = useState<UseCaseTrend | null>(null);

  /* AI grounded summary */
  const [aiSummary, setAiSummary]       = useState<string | null>(null);
  const acTrends = useAutocomplete(searchQuery);
  const [aiLoading, setAiLoading]       = useState(false);
  const [, setShowSources]   = useState(false);

  /* prevent duplicate AI call per search */
  const queriedForRef = useRef<string | null>(null);

  // Pre-fill and auto-search when navigated from chat
  useEffect(() => {
    const q = location.state?.search_query;
    if (!q) return;
    window.history.replaceState({}, document.title);
    runSearch(q.trim());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const activeQueryRef = useRef<string>('');

  /* ── trigger grounded AI once Serper results arrive ── */
  useEffect(() => {
    if (!activeQueryRef.current) return;
    if (queriedForRef.current === activeQueryRef.current) return;
    if (webSearch.results.length === 0) return;
    if (webSearch.phase !== 'enriched' && webSearch.phase !== 'done') return;

    queriedForRef.current = activeQueryRef.current;
    setAiLoading(true);

    const sourcesForAI = webSearch.results.slice(0, 10).map(r => ({
      title: r.title, url: r.url, description: r.description,
      content: r.content, source: r.source,
    }));

    axios
      .post(`${BASE_URL}/api/v1/chat/grounded`, {
        query: `Summarise the latest AI use case trends for: ${activeQueryRef.current}. Focus on real-world adoption, key players, and momentum.`,
        web_results: sourcesForAI,
      })
      .then(res => setAiSummary((res.data.answer as string) || null))
      .catch(() => setAiSummary(null))
      .finally(() => setAiLoading(false));
  }, [webSearch.results, webSearch.phase]);

  /* ── main search ── */
  const runSearch = async (overrideQuery?: string, overrideGeo?: string) => {
    const q   = (overrideQuery ?? searchQuery).trim();
    const geo = overrideGeo ?? geography;
    if (!q && !sector) return;

    // sync state if called from quick prompt
    if (overrideQuery !== undefined) setSearchQuery(overrideQuery);
    if (overrideGeo   !== undefined) setGeography(overrideGeo);

    setLoading(true);
    setHasSearched(true);
    setSelectedTrend(null);
    setAiSummary(null);
    setShowSources(false);
    queriedForRef.current = null;

    const fullQuery = [q, sector, geo].filter(Boolean).join(' ');
    activeQueryRef.current = fullQuery;

    /* ── parallel: web search + backend trends ── */
    const countryCode = GEO_CODE[geo] || 'IN';
    webSearch.reset();
    webSearch.search(fullQuery, countryCode, 20, 5);   // Serper → Brave → Tavily

    try {
      const params = new URLSearchParams();
      if (sector) params.append('sector', sector);
      if (q)      params.append('search_query', q);
      if (geo)    params.append('geography', geo);

      const res  = await fetch(`${STARTUP_API_URL}/api/v1/usecases/trends?${params}`, {
        headers: { 'Authorization': localStorage.getItem('authToken') || '' },
      });
      const data = await res.json();
      if (data.status === 'success') setTrends(data.data || []);
    } catch (err) {
      console.error('Trends API error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPrompt = (query: string, geo: string) =>
    runSearch(query, geo);

  /* ── style helpers ── */
  const getMomentumIcon = (m?: string) => {
    if (!m) return <FiBarChart2 className="text-gray-400" />;
    if (/rising|strong/i.test(m))   return <FiTrendingUp  className="text-green-400" />;
    if (/declining|weak/i.test(m))  return <FiTrendingDown className="text-red-400"  />;
    return <FiBarChart2 className="text-yellow-400" />;
  };

  const getScoreColor = (s?: number) => {
    if (!s) return 'text-gray-400';
    if (s >= 80) return 'text-green-400';
    if (s >= 60) return 'text-yellow-400';
    if (s >= 40) return 'text-orange-400';
    return 'text-red-400';
  };

  const getAdoptionColor = (stage?: string) => {
    switch (stage?.toLowerCase()) {
      case 'emerging': return 'bg-purple-500/20 text-purple-400';
      case 'growing':  return 'bg-green-500/20 text-green-400';
      case 'maturing': return 'bg-blue-500/20 text-blue-400';
      case 'mature':   return 'bg-gray-500/20 text-gray-400';
      default:         return 'bg-white/10 text-gray-400';
    }
  };

  const isSearching = webSearch.phase === 'serper' || webSearch.phase === 'enriched';

  /* ══════════════════════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      <section className="pt-32 pb-12 px-4 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto">

          {/* ── Header ── */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-3 py-1.5 mb-4 rounded-lg hover:bg-white/10 transition-all border border-white/10"
          >
            ← Back to Chat
          </button>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">
            Use Case <span className="text-[#FF541F]">Trends</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Discover the latest 2025 AI use case trends — powered by live web search
          </p>

          {/* ── Search Controls ── */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div className="md:col-span-2 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); acTrends.setShowSuggestions(true); }}
                onKeyDown={e => acTrends.handleKeyDown(e, s => { setSearchQuery(s); acTrends.clear(); runSearch(); }, runSearch)}
                onBlur={() => setTimeout(() => acTrends.clear(), 150)}
                placeholder="Search trends (e.g., AI agents, digital twins…)"
                className="w-full px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white placeholder-white/25 focus:outline-none focus:border-[#FF541F]"
              />
              {acTrends.showSuggestions && acTrends.suggestions.length > 0 && (
                <ul className="absolute top-full left-0 right-0 mt-1 z-50 rounded-xl overflow-hidden border border-white/10 shadow-2xl" style={{ background: "#1a1a1a" }}>
                  {acTrends.suggestions.map((s, i) => (
                    <li key={s} onMouseDown={() => { setSearchQuery(s); acTrends.clear(); runSearch(); }}
                      className="px-4 py-2.5 text-sm cursor-pointer transition-colors"
                      style={{ background: i === acTrends.activeSuggestion ? "rgba(255,84,31,0.15)" : "transparent", color: i === acTrends.activeSuggestion ? "#FF541F" : "#d1d5db" }}>
                      {s}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <select
              value={sector}
              onChange={e => setSector(e.target.value)}
              className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
            >
              <option value="">All Sectors</option>
              {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select
              value={geography}
              onChange={e => setGeography(e.target.value)}
              className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
            >
              {GEOGRAPHIES.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>

          {/* Discover button */}
          <button
            onClick={() => runSearch()}
            disabled={loading || isSearching}
            className="w-full md:w-auto px-8 py-3 bg-[#FF541F] rounded-lg hover:bg-[#FF541F]/80 transition-all disabled:opacity-50 flex items-center gap-2 mb-8"
          >
            {loading || isSearching
              ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Searching…</>
              : <><FiSearch size={17} /> Discover Trends {geography && <span className="opacity-70 text-sm">· {geography}</span>}</>
            }
          </button>


          {/* ── Main 2-col layout ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Trends List */}
            <div className="lg:col-span-2">

              {/* loading */}
              {loading && (
                <div className="py-12 bg-[#1A1A1A] rounded-xl border border-white/10">
                  <RippleWaveLoader messages={TRENDS_LOADING_MESSAGES} />
                </div>
              )}

              {/* empty / prompt state */}
              {!loading && !hasSearched && (
                <div className="bg-[#1A1A1A] rounded-xl p-8 border border-white/10 text-center">
                  <FiZap size={48} className="mx-auto mb-4 text-[#FF541F]" />
                  <h3 className="text-xl font-semibold mb-2">Discover Latest 2025 Trends</h3>
                  <p className="text-gray-400 mb-2 text-sm">
                    Type a query and click <strong>Discover Trends</strong> — or pick a quick prompt below
                  </p>
                  <p className="text-xs text-gray-600 mb-5 flex items-center justify-center gap-1">
                    <FiMapPin size={11} />
                    Defaults to <span className="text-orange-400 font-medium ml-1">India</span>
                    <span className="mx-1">·</span>
                    powered by Serper · Brave · Tavily
                  </p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {QUICK_PROMPTS.map(p => (
                      <button
                        key={p.label}
                        onClick={() => handleQuickPrompt(p.query, p.geo)}
                        className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-orange-500/40 rounded-lg text-sm transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!loading && hasSearched && trends.length === 0 && (
                <div className="text-center py-12 bg-[#1A1A1A] rounded-xl border border-white/10">
                  <p className="text-gray-400">No trends found. Try a different query or sector.</p>
                </div>
              )}

              {!loading && trends.length > 0 && (
                <div className="space-y-4">
                  {trends.map((trend, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => setSelectedTrend(trend)}
                      className={`bg-[#1A1A1A] rounded-xl p-6 border transition-all cursor-pointer ${
                        selectedTrend?.usecase_title === trend.usecase_title
                          ? 'border-[#FF541F]'
                          : 'border-white/10 hover:border-[#FF541F]/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getMomentumIcon(trend.momentum)}
                          <h3 className="text-lg font-semibold">{trend.usecase_title}</h3>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="px-2 py-0.5 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded-full font-medium">2025</span>
                          {trend.trend_score !== undefined && (
                            <span className={`text-xl font-bold ${getScoreColor(trend.trend_score)}`}>
                              {trend.trend_score}
                            </span>
                          )}
                        </div>
                      </div>

                      <p className="text-gray-400 text-sm mb-4 line-clamp-2">{trend.description}</p>

                      <div className="flex flex-wrap gap-2">
                        {trend.sector && (
                          <span className="px-2 py-1 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">{trend.sector}</span>
                        )}
                        {trend.adoption_stage && (
                          <span className={`px-2 py-1 text-xs rounded ${getAdoptionColor(trend.adoption_stage)}`}>
                            {trend.adoption_stage}
                          </span>
                        )}
                        {trend.momentum && (
                          <span className="px-2 py-1 text-xs bg-white/10 text-gray-400 rounded">{trend.momentum}</span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Detail Panel */}
            <div className="lg:col-span-1">
              {selectedTrend ? (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 sticky top-24"
                >
                  <h3 className="text-xl font-semibold mb-4">{selectedTrend.usecase_title}</h3>

                  <div className="space-y-4">
                    {selectedTrend.trend_score !== undefined && (
                      <div className="bg-black/30 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-gray-400">Trend Score</span>
                          <span className={`text-2xl font-bold ${getScoreColor(selectedTrend.trend_score)}`}>
                            {selectedTrend.trend_score}/100
                          </span>
                        </div>
                        <div className="w-full bg-white/10 rounded-full h-2">
                          <div className="bg-[#FF541F] h-2 rounded-full transition-all"
                            style={{ width: `${selectedTrend.trend_score}%` }} />
                        </div>
                      </div>
                    )}

                    {(selectedTrend.market_size || selectedTrend.growth_rate) && (
                      <div className="grid grid-cols-2 gap-3">
                        {selectedTrend.market_size && (
                          <div className="bg-black/30 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Market Size</p>
                            <p className="text-sm font-medium">{selectedTrend.market_size}</p>
                          </div>
                        )}
                        {selectedTrend.growth_rate && (
                          <div className="bg-black/30 rounded-lg p-3">
                            <p className="text-xs text-gray-500 mb-1">Growth Rate</p>
                            <p className="text-sm font-medium text-green-400">{selectedTrend.growth_rate}</p>
                          </div>
                        )}
                      </div>
                    )}

                    {selectedTrend.prediction_timeframe && (
                      <div className="flex items-center gap-2 text-sm">
                        <FiClock className="text-gray-400" />
                        <span className="text-gray-400">Timeframe:</span>
                        <span>{selectedTrend.prediction_timeframe}</span>
                      </div>
                    )}

                    {selectedTrend.drivers && selectedTrend.drivers.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <FiTrendingUp className="text-green-400" /> Key Drivers
                        </h4>
                        <ul className="space-y-1">
                          {selectedTrend.drivers.map((d, i) => (
                            <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                              <span className="text-green-400 shrink-0">+</span>{d}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedTrend.barriers && selectedTrend.barriers.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <FiTrendingDown className="text-red-400" /> Barriers
                        </h4>
                        <ul className="space-y-1">
                          {selectedTrend.barriers.map((b, i) => (
                            <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                              <span className="text-red-400 shrink-0">-</span>{b}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedTrend.key_players && selectedTrend.key_players.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2">Key Players</h4>
                        <div className="flex flex-wrap gap-1">
                          {selectedTrend.key_players.map((p, i) => (
                            <span key={i} className="px-2 py-1 text-xs bg-white/5 text-gray-400 rounded">{p}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              ) : (
                <div className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 text-center">
                  <FiBarChart2 size={32} className="mx-auto mb-3 text-gray-600" />
                  <p className="text-gray-400 text-sm">Select a trend to view details</p>
                </div>
              )}
            </div>
          </div>

          {/* ── AI Trend Summary — shown below the trend grid ── */}
          {hasSearched && (aiLoading || aiSummary) && (
            <div
              className="mt-8 p-5 rounded-xl border"
              style={{ background: 'rgba(255,84,31,0.05)', borderColor: 'rgba(255,84,31,0.2)' }}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center text-white text-[10px] font-bold"
                  style={{ background: '#FF541F' }}>AI</div>
                <span className="text-sm font-medium text-orange-300">AI Trend Summary</span>
              </div>
              {aiLoading ? (
                <div className="flex items-center gap-2">
                  {[0,1,2].map(j => (
                    <span key={j} className="block w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                      style={{ animationDelay: `${j * 0.15}s` }} />
                  ))}
                  <span className="text-xs text-gray-500">Synthesising from web sources…</span>
                </div>
              ) : aiSummary ? (
                <>
                  <div className="prose-sm">
                    <ReactMarkdown
                      components={{
                        h1: ({children}) => <h1 className="text-white font-bold text-base mb-2 mt-0">{children}</h1>,
                        h2: ({children}) => <h2 className="text-white font-semibold text-sm mb-2 mt-4 pb-1 border-b border-white/10">{children}</h2>,
                        h3: ({children}) => <h3 className="text-orange-300 font-semibold text-sm mb-1 mt-3">{children}</h3>,
                        p: ({children}) => <p className="text-gray-300 text-sm leading-relaxed mb-2 last:mb-0">{children}</p>,
                        strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                        em: ({children}) => <em className="text-gray-300 italic">{children}</em>,
                        ul: ({children}) => <ul className="list-disc list-inside space-y-1 mb-3 text-sm text-gray-300">{children}</ul>,
                        ol: ({children}) => <ol className="list-decimal list-inside space-y-1 mb-3 text-sm text-gray-300">{children}</ol>,
                        li: ({children}) => <li className="text-gray-300 text-sm leading-relaxed">{children}</li>,
                        hr: () => <hr className="border-white/10 my-4" />,
                        code: ({children}) => <code className="bg-white/10 px-1.5 py-0.5 rounded text-xs text-orange-300 font-mono">{children}</code>,
                        blockquote: ({children}) => <blockquote className="border-l-2 border-orange-500/50 pl-3 my-2 text-gray-400 italic text-sm">{children}</blockquote>,
                      }}
                    >
                      {aiSummary}
                    </ReactMarkdown>
                  </div>
                  {webSearch.results.length > 0 && (
                    <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-white/5">
                      {webSearch.results.slice(0, 5).map((r, idx) => (
                        <a key={idx} href={r.url} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-orange-400 transition-colors">
                          <span className="text-orange-500 font-semibold">[{idx+1}]</span>
                          <span className="truncate max-w-[100px]">{hostLabel(r.url)}</span>
                          <FiExternalLink size={9} />
                        </a>
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UseCaseTrendsPage;
